import { useState } from 'react';
import coordId from './coordId';
import { formatCoord, shortId } from './format';

// A small chevron, drawn (not a glyph) so it renders identically everywhere.
const Chevron = ({ up }) => (
    <svg
        className="chevron"
        width="10" height="10" viewBox="0 0 10 10"
        fill="none" aria-hidden="true"
        style={up ? { transform: 'rotate(180deg)' } : undefined}
    >
        <path d="M1 3.5 L5 7 L9 3.5" stroke="currentColor" strokeWidth="1.3"
              strokeLinecap="round" strokeLinejoin="round" />
    </svg>
);

// The keep-mark, also drawn rather than a glyph. Outline = not kept, filled = kept,
// so the state reads at a glance without relying on the label alone.
const Mark = ({ filled }) => (
    <svg
        className="save-mark"
        width="10" height="10" viewBox="0 0 10 10"
        aria-hidden="true"
    >
        <path
            d="M5 0.7 L6.3 3.7 L9.3 5 L6.3 6.3 L5 9.3 L3.7 6.3 L0.7 5 L3.7 3.7 Z"
            fill={filled ? 'currentColor' : 'none'}
            stroke="currentColor"
            strokeWidth="0.9"
            strokeLinejoin="round"
        />
    </svg>
);

// The signature element: a museum specimen label pinned to the current image.
// It shows the exact complex-plane region and its short id — the coordinates
// ARE the image's identity (the Phase 2 uniqueness key). "Unclaimed" is a
// dormant hook for the future claim/buy flow.
//
// Collapsible so it never permanently blocks part of the image on small screens
// (the label is interactive, so you can't start a drag through it). Collapsed,
// it stays as a chip showing the id — identity is always on screen.
// `manualZoom`, `onToggleSave` and the save state are all optional: the gallery
// reuses this placard to caption a saved specimen, where there is nothing to
// re-render and nothing to save.
const Placard = ({ current, manualZoom, saved, saveError, onToggleSave }) => {

    const [collapsed, setCollapsed] = useState(false);
    const [showCoords, setShowCoords] = useState(false);

    const { realMin, realMax, imaginaryMin, imaginaryMax } = current;
    const id = coordId(current);

    if (collapsed) {
        return (
            <button
                id="placard-chip"
                type="button"
                onClick={() => setCollapsed(false)}
                aria-label="Show specimen details"
            >
                <Chevron up />
                <span className="placard-label">Specimen</span>
                <span className="placard-id">{shortId(id)}</span>
                {saved && <Mark filled />}
            </button>
        );
    }

    return (
        <div id="placard">
            <div className="placard-head">
                <span className="placard-label">Specimen</span>
                <span className="placard-head-right">
                    <span className="placard-id">{shortId(id)}</span>
                    <button
                        type="button"
                        className="placard-hide"
                        onClick={() => setCollapsed(true)}
                        aria-label="Hide specimen details"
                    >
                        <Chevron />
                    </button>
                </span>
            </div>

            <div className="placard-row">
                <i className="placard-axis">&#8477;</i>
                <span className="placard-value">{formatCoord(realMin)} &rarr; {formatCoord(realMax)}</span>
            </div>
            <div className="placard-row">
                <i className="placard-axis">&#120050;</i>
                <span className="placard-value">{formatCoord(imaginaryMin)} &rarr; {formatCoord(imaginaryMax)}</span>
            </div>

            {/* The tier row. "Unclaimed" and "Save" sit together on purpose: Save
                is the free bottom rung of the Save → Hold → Own ladder, so the
                free action and the state it will eventually change live in one
                place rather than the ladder appearing out of nowhere later. */}
            <div className="placard-foot">
                <span className="placard-tag">Unclaimed</span>
                {onToggleSave &&
                    <button
                        type="button"
                        className={`placard-save${saved ? ' is-saved' : ''}`}
                        aria-pressed={!!saved}
                        onClick={onToggleSave}
                        title={saved ? 'Remove from your gallery' : 'Keep this specimen in your gallery'}
                    >
                        <Mark filled={saved} />
                        {saved ? 'Saved' : 'Save'}
                    </button>
                }
                {manualZoom &&
                    <button
                        type="button"
                        className="placard-toggle"
                        aria-expanded={showCoords}
                        onClick={() => setShowCoords(s => !s)}
                    >
                        {showCoords ? 'Close' : 'Enter coordinates'}
                    </button>
                }
            </div>

            {saveError &&
                <p className="placard-error" role="status">
                    Couldn&rsquo;t save &mdash; browser storage is full or blocked.
                </p>
            }

            {showCoords &&
                <div className="coord-form">
                    <label htmlFor="real-min-in">&#8477; min</label>
                    <input id="real-min-in" name="real-min-in" type="number" step="0.0001" placeholder={formatCoord(realMin)} />
                    <label htmlFor="real-max-in">&#8477; max</label>
                    <input id="real-max-in" name="real-max-in" type="number" step="0.0001" placeholder={formatCoord(realMax)} />
                    <label htmlFor="imaginary-min-in">&#120050; min</label>
                    <input id="imaginary-min-in" name="imaginary-min-in" type="number" step="0.0001" placeholder={formatCoord(imaginaryMin)} />
                    <label htmlFor="imaginary-max-in">&#120050; max</label>
                    <input id="imaginary-max-in" name="imaginary-max-in" type="number" step="0.0001" placeholder={formatCoord(imaginaryMax)} />
                    <button type="button" className="btn-render" onClick={() => manualZoom()}>Render</button>
                </div>
            }
        </div>
    );
};

export default Placard;
